import React, { useState, useEffect, useRef } from "react";
import { useLocation, NavLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { authAction } from '../src/redux/modules';

import Router from './pages/Router';
import { useHandleRouteWhenAuth } from './hooks';
import CookieBanner from './components/CookieBanner/CookieBanner';
import ChatWidget from './components/ChatWidget';
import WelcomePopup from './components/WelcomePopup';
import CommercialConsentModal from './components/CommercialConsentModal';
import SalesRepCodePopup from './components/SalesRepCodePopup';
import { API } from '@services/http.service';

// Login sonrası modalları sırayla yönetir:
//   1) Açık Rıza Ticari İleti İzni  →  2) Hoşgeldin popup  →  3) Satış temsilcisi kodu popup'ı
// Modalların hepsi disclaimer akışının tamamlanmasını ayrıca bekler (consentDone bunu da kapsar:
// izin modalı ancak disclaimer çözüldükten sonra "resolved" olur).
//
// "İLK GİRİŞ" TEK BAYRAKLA YÖNETİLİR: auth_user.welcome_popup_seen. Durum burada BİR KEZ sorulur
// (/auth/secure/welcomePopup/status) ve hem hoşgeldin popup'ına hem satış temsilcisi popup'ına
// verilir; "görüldü" işareti (POST .../seen) ise ancak bu iki modaldan EN AZ BİRİ gerçekten
// gösterildiyse atılır. Böylece satış temsilcisi popup'ı için ayrı bir kolon açmaya gerek kalmaz.
// (Yan etki: hoşgeldin görseli yokken satış temsilcisi popup'ı çıkan bir işletme, görsel sonradan
// yüklenirse hoşgeldin popup'ını görmez — tek bayrak kullanmanın doğal sonucu.)
const PostLoginModals = () => {
  const [consentDone, setConsentDone] = useState(false);
  const [welcomeDone, setWelcomeDone] = useState(false);
  // null = henüz bilinmiyor, true/false = kullanıcının ilk girişi mi?
  const [firstLogin, setFirstLogin] = useState(null);
  // "görüldü" isteği yalnızca bir kez gitsin.
  const seenSentRef = useRef(false);

  // Modallardan biri gerçekten gösterildiğinde çağrılır → ilk-giriş bayrağı kapatılır.
  const markFirstLoginSeen = () => {
    if (seenSentRef.current) return;
    seenSentRef.current = true;
    API.post("/auth/secure/welcomePopup/seen").catch(() => {});
  };

  useEffect(() => {
    // Önceki modal (izin onayı) çözülmeden sorma; o modal disclaimer'ı da beklediği için
    // consentDone true olduğunda disclaimer akışı da tamamlanmış olur.
    if (!consentDone || firstLogin !== null) return;
    let active = true;
    (async () => {
      try {
        const response = await API.get("/auth/secure/welcomePopup/status");
        if (active) setFirstLogin(response?.data === true);
      } catch (error) {
        // Durum alınamazsa hiçbir popup gösterilmez.
        if (active) setFirstLogin(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [consentDone, firstLogin]);

  return (
    <>
      {/* Açık Rıza Ticari İleti İzni: SMS izni kapalı & karar verilmemiş kullanıcıda çıkar */}
      <CommercialConsentModal onResolved={() => setConsentDone(true)} />
      {/* Hoşgeldin popup: admin görsel yüklediyse kullanıcının ilk girişinde açılır */}
      <WelcomePopup
        enabled={consentDone}
        firstLogin={firstLogin}
        onShown={markFirstLoginSeen}
        onResolved={() => setWelcomeDone(true)}
      />
      {/* Satış temsilcisi kodu: yalnızca işletme (USER2) rolünde, kayıtta kod girilmemişse,
          ilk girişte bir kez çıkar. Opsiyoneldir; sırada EN SON gelir. */}
      <SalesRepCodePopup
        enabled={consentDone && welcomeDone}
        firstLogin={firstLogin}
        onShown={markFirstLoginSeen}
      />
    </>
  );
};

const App = () => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((store) => store.auth.isAuthenticated);

  const rgbToHex = (r, g, b) => {
    return (
      "#" +
      ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()
    );
  };

  // Example Usage
  // rgbToHex(227, 242, 253); // #E3F2FD

  const hexToRgb = (hex) => {
    // '#' işaretini kaldırıyoruz
    let cleanedHex = hex.replace(/^#/, "");

    // Eğer hex kodu üç basamaklıysa (örneğin #FFF), bunu altı basamaklı hale getiriyoruz
    if (cleanedHex.length === 3) {
      cleanedHex = cleanedHex
        .split("")
        .map((c) => c + c)
        .join("");
    }

    // Hex'i RGB formatına çeviriyoruz
    let r = parseInt(cleanedHex.slice(0, 2), 16);
    let g = parseInt(cleanedHex.slice(2, 4), 16);
    let b = parseInt(cleanedHex.slice(4, 6), 16);

    return `${r}, ${g}, ${b}`;
  };

  // Example Usage
  // hexToRgb('#E3F2FD');

  useEffect(() => {
    let siteURL = window.location.origin;

    // Eğer URL http ile başlıyorsa ve localhost ise, https ile değiştir
    if (
      siteURL.startsWith("http://") &&
      window.location.hostname === "localhost"
    ) {
      siteURL = siteURL.replace("http://", "https://");
    }

    // API'den login ayarlarını alıyoruz
    dispatch(authAction.getLoginSettings(siteURL))
      .then((res) => {
        if (res.isHttpSuccess) {
          const { data } = res;

          // Gelen renkleri CSS değişkenleri olarak güncelliyoruz
          const colors = {
            "--bs-blue-lighter-rgb": hexToRgb(data.blueLighter),
            "--bs-blue-light-rgb": hexToRgb(data.blueLight),
            "--bs-blue-rgb": hexToRgb(data.blue),
            "--bs-blue-dark-rgb": hexToRgb(data.blueDark),

            "--bs-blue-lighter": data.blueLighter,
            "--bs-blue-light": data.blueLight,
            "--bs-blue": data.blue,
            "--bs-blue-dark": data.blueDark,
          };

          // Renk değişkenlerini güncelle
          Object.keys(colors).forEach((key) => {
            document.documentElement.style.setProperty(key, colors[key]);
          });

          // Renkleri cache'le ki sayfa yenilendiğinde ilk boyamadan önce
          // (index.html'deki inline script ile) uygulanabilsin ve renk
          // titremesi (flash) olmasın
          localStorage.setItem("loginSettingColors", JSON.stringify(colors));

          // Gelen `pageTitle` değerini `<title>` etiketine ekle
          if (data.pageTitle) {
            document.title = data.pageTitle;
          }

          // `metaDescription` alanını `<meta name="description">` olarak güncelle
          if (data.metaDescription) {
            let metaDescriptionTag = document.querySelector(
              'meta[name="description"]'
            );

            // Eğer meta description tag yoksa yeni bir tane oluştur
            if (!metaDescriptionTag) {
              metaDescriptionTag = document.createElement("meta");
              metaDescriptionTag.name = "description";
              document.head.appendChild(metaDescriptionTag);
            }

            // Meta description içeriğini güncelle
            metaDescriptionTag.content = data.metaDescription.replace(
              /<meta name='description' content='(.*?)' \/>/,
              "$1"
            );
          }

          // `favicon` alanını güncelle, favicon tag'ini kontrol et ve güncelle
          if (data.favicon) {
            let faviconLink = document.querySelector('link[rel="icon"]');

            // Eğer favicon yoksa yeni bir tane oluştur
            if (!faviconLink) {
              faviconLink = document.createElement("link");
              faviconLink.rel = "icon";
              document.head.appendChild(faviconLink);
            }

            // Favicon URL'sini güncelle
            faviconLink.href = data.favicon;
          }
        }
      })
      .catch((err) => {
        console.log("Hata: ", err);
      });
  }, [dispatch]);

  // useEffect(() => {
  //     // Renkleri güncellemek için CSS değişkenlerini JavaScript ile güncelliyoruz
  //     document.documentElement.style.setProperty('--bs-blue-lighter-rgb', '227, 242, 253');
  //     document.documentElement.style.setProperty('--bs-blue-light-rgb', '25, 118, 210');
  //     document.documentElement.style.setProperty('--bs-blue-rgb', '27, 97, 172');
  //     document.documentElement.style.setProperty('--bs-blue-dark-rgb', '13, 71, 161');

  //     document.documentElement.style.setProperty('--bs-blue-lighter', '#E3F2FD');
  //     document.documentElement.style.setProperty('--bs-blue-light', '#1976D2');
  //     document.documentElement.style.setProperty('--bs-blue', '#1B61AC');
  //     document.documentElement.style.setProperty('--bs-blue-dark', '#0D47A1');
  // }, []);

  useHandleRouteWhenAuth();
  return (
    <>
      <Router />
      <CookieBanner />
      {/* Login sonrası modallar: Hoşgeldin popup → Açık Rıza Ticari İleti İzni (sıralı) */}
      {isAuthenticated && <PostLoginModals />}
      {/* Portal asistanı (chatbot): login sonrası her ekranda sağ altta görünür.
          isAuthenticated redux-persist'ten gelir, token ise ayrı bir localStorage
          anahtarıdır; ikisi ayrışabildiği için token'ı da ayrıca kontrol ediyoruz.
          Aksi halde widget token'sız açılır ve asistan isteği kimliksiz gider. */}
      {isAuthenticated && localStorage.getItem("token") && <ChatWidget />}
    </>
  );
};

export default App;
